/**
 * Ranking-term copy SSOT. Every definition shown in a tooltip lives here and is
 * paired with docs/RANKING_TERMS.md (the human-readable glossary; commandertemplate.com
 * is credited there for the Value Units / four-facet framing). Edit both together.
 */

export type TermKey =
  | 'speed'
  | 'consistency'
  | 'interaction'
  | 'closing'
  | 'closure'
  | 'synergy'
  | 'lens_score'
  | 'engines_online'
  | 'power_scale'
  | 'bracket'
  | 'role_value'
  | 'role_quality'
  | 'plan'
  | 'profile'
  | 'salt'
  | 'budget'
  | 'reads_as'
  | 'theme'
  | 'popularity'
  | 'blend_score'
  | 'spicy'
  | 'combo_completion'
  | 'role_need'
  | 'goal_target'
  | 'trial_goals'
  | 'inputs_staleness'
  | 'deck_budget'
  | 'eval_margin'
  | 'cut_damage';

export interface RankingTerm {
  label: string;
  /** What it means. */
  definition: string;
  /** How it is computed, and what 0 / n/a means. */
  computed: string;
  caveat?: string;
}

export type SynergyPresence = 'measured' | 'estimated' | 'coplay' | 'not_listed' | 'page_unavailable';

/** Presence states whose synergy value is a real number (shown, not "n/a"). */
export const SYNERGY_HAS_VALUE: ReadonlySet<SynergyPresence> = new Set<SynergyPresence>(['measured', 'estimated', 'coplay']);

export const SYNERGY_PRESENCE_COPY: Record<Exclude<SynergyPresence, 'measured'>, string> = {
  estimated: "estimated from this card's mechanics — no EDHREC data yet",
  coplay: 'from co-play across the corpus of decks',
  not_listed: "not on EDHREC's list for this commander - common for new cards",
  page_unavailable: 'no EDHREC data for this commander',
};

/** Small marker beside an estimated synergy value. */
export const SYNERGY_ESTIMATED_MARKER = 'est.';

export interface SynergyReceipt {
  context_tag: string;
  card_tag: string;
}

const SYNERGY_RECEIPTS_MAX = 3;

/** Receipts line for an estimated synergy: "estimated from: a ↔ b, c ↔ d" (slugs humanized, max 3 pairs). */
export function synergyReceiptsLine(receipts: readonly SynergyReceipt[]): string {
  const human = (slug: string) => slug.replace(/-/g, ' ');
  const pairs = receipts
    .slice(0, SYNERGY_RECEIPTS_MAX)
    .map((r) => `${human(r.context_tag)} ↔ ${human(r.card_tag)}`)
    .join(', ');
  return `estimated from: ${pairs}`;
}

const COPLAY_RECEIPTS_MAX = 5;

/** Receipts line for a co-play synergy: the deck cards that most predict the candidate, by name
 *  ("co-play with: Sol Ring, Arcane Signet"). The wire pairs are (deck card name, 'co-play'); names
 *  are shown as-is (a slug humanizer would mangle hyphenated names). */
export function coplayReceiptsLine(receipts: readonly SynergyReceipt[]): string {
  return `co-play with: ${receipts.slice(0, COPLAY_RECEIPTS_MAX).map((r) => r.context_tag).join(', ')}`;
}

/** Co-play looked at the deck and gave no score: one line per reason, shown under the presence copy so a reader
 *  never mistakes "est." or EDHREC's number for co-play having nothing to say. */
export const COPLAY_ABSTAINED_COPY = {
  no_column: 'Co-play has no data for this card: too few decks in the corpus run it.',
  low_coverage: 'Co-play knows too little of this deck (under half its nonland cards are known to the co-play model), so it stepped aside.',
  no_hits: (neighbors: readonly string[]) =>
    `Co-play looked and stepped aside: none of this deck's nonland cards are among the card's strongest co-play neighbours${
      neighbors.length ? ` (${neighbors.join(', ')})` : ''
    }.`,
} as const;

export function coplayAbstainedLine(ab: { reason: 'no_column' | 'low_coverage' | 'no_hits'; neighbors: readonly string[] }): string {
  return ab.reason === 'no_hits' ? COPLAY_ABSTAINED_COPY.no_hits(ab.neighbors) : COPLAY_ABSTAINED_COPY[ab.reason];
}

/** Where a facet's target came from, for the chip's tooltip. The bracket medians come from one shipped
 *  snapshot of analyzed decks (not the viewer's own), so the copy never says "your". '' when unknown. */
export function targetSourcePhrase(source: 'goal' | 'fence_median' | null, bracket: number | null): string {
  if (source === 'goal') return 'your stated goal';
  if (source === 'fence_median') return bracket === null ? 'the bracket median' : `the median of analyzed B${bracket} decks`;
  return '';
}

/** Below this, a level change rounds to 0.00 at the closure's two decimals. */
const LEVEL_EPSILON = 0.005;

/** The line that justifies a facet chip's number: the deck's level before and after the card, against the
 *  target the closure was measured on. Levels are facet points on the 0-10 scale, at the closure's two
 *  decimals so the movement shown adds up to the number on the chip. */
export function facetLevelLine(level: {
  before: number; after: number; target: number | null; source: 'goal' | 'fence_median' | null; bracket: number | null;
}): string {
  const f = (v: number) => v.toFixed(2);
  const move = `deck ${f(level.before)} → ${f(level.after)} of 10`;
  const unchanged = Math.abs(level.after - level.before) < LEVEL_EPSILON;
  if (level.target === null) return `${move} · no target for this facet, so there is nothing to close`;
  const phrase = targetSourcePhrase(level.source, level.bracket);
  const target = `target ${f(level.target)}${phrase ? ` (${phrase})` : ''}`;
  if (level.before >= level.target && level.after >= level.target) {
    return `${move} · already at or above the ${target}: nothing to close`;
  }
  if (unchanged) return `${move} · ${target} · this card adds nothing here`;
  if (level.before < level.target && level.after >= level.target) return `${move} · ${target} · closes the whole gap`;
  return `${move} · ${target}`;
}

/** The reads-as position on the wire ('low' | 'mid' | 'high') as a reader says it. */
export const READS_AS_POSITION_COPY: Record<string, string> = {
  low: 'lower third',
  mid: 'middle third',
  high: 'upper third',
};

/** The sample behind a reads-as line, for its tooltip (n lives here, not on the line). The decks compared
 *  against are one shipped snapshot of analyzed decks, not the viewer's own. */
export function readsAsSampleLine(n: number, bracket: number): string {
  return `compared with ${n} analyzed B${bracket} decks`;
}

export const RANKING_TERMS: Record<TermKey, RankingTerm> = {
  speed: {
    label: 'Speed',
    definition: 'The Speed facet (0-10; distinct from the fast/mid/slow Speed category): how quickly the deck gets going.',
    computed:
      'Computed from how low the average nonland mana value is, plus how much cheap, real mana ramp it has. A missing card profile makes it "degraded" rather than zero.',
    caveat: 'Not the same as the plain "Speed" label (fast / mid / slow) under the deck banner, which is a separate summary.',
  },
  consistency: {
    label: 'Consistency',
    definition: 'How reliably the deck does its thing.',
    computed: "Computed from card-draw quality, number of tutors, and how many cards fit the deck's theme.",
    caveat: 'Draw quality uses Role Value.',
  },
  interaction: {
    label: 'Interaction',
    definition: 'How much and how quickly the deck can answer opponents.',
    computed:
      'Computed from the count of interaction cards, with faster ones counting more, against what a deck of this bracket usually wants.',
    caveat:
      'This score currently tops out at 10 for most decks, so it separates weak from average but not average from strong.',
  },
  closing: {
    label: 'Closing',
    definition: 'How well the deck ends the game.',
    computed: 'Computed from combo strength, win-condition count and how focused the deck is on its commander.',
    caveat:
      'Also tops out easily. Closing replaces the "Lethality" facet in commandertemplate.com\'s framing and is calculated differently.',
  },
  closure: {
    label: 'Closure',
    definition:
      "How much adding this card would shrink a shortfall in that facet, relative to where the deck's bracket usually sits.",
    computed:
      'Computed as the gap to target before adding the card minus the gap after. A positive number closes a gap; 0.00 means it changes nothing there, not that it is bad. Being above target is never penalized.',
    caveat:
      "In facet points on the 0-10 facet scale. Each chip's tooltip shows the deck's level before and after the card and the target it was measured against, so two chips' numbers can be compared.",
  },
  synergy: {
    label: 'Synergy',
    definition: "How strongly EDHREC's data ties this card to your commander, or how often it is played with your deck's cards.",
    computed:
      "Computed as the card's appearance lift on EDHREC's page for that commander, or, when the co-play model knows enough of the deck, from co-play across the corpus of decks. A measured 0.00 means listed, but no lift. In the builder's Suggestions this term is a label: the ranking is half EDHREC inclusion and half co-play from the deck's cards (co-play alone when there is no page), and the chip shows the co-play value. Spicy picks, low-inclusion cards (under 15% of this commander's decks) whose co-play with your cards clears a measured bar, keep their place when they rank in the list on their own, and are added at the end when their low inclusion would otherwise keep them out; usually none appear.",
    caveat:
      "EDHREC's stored page only lists its top cards, so an unlisted card scores nothing here without being bad. In Find a Home, if there is no page for the commander, synergy is left out of the ordering rather than counted as zero (the builder ranks by co-play alone then). When co-play looked and gave no score, the tooltip says why it stepped aside.",
  },
  lens_score: {
    label: 'Lens score',
    definition:
      'In Find a Home, the number that ranks your candidate home decks for the card you searched (the card is not being ranked; your decks are).',
    computed: "Computed as total closure + synergy x the presence's weight (0.5 for EDHREC; for co-play, the admin setting, 0.5 by default).",
    caveat: 'At the default weight (0.5), synergy can reorder near-ties but cannot outweigh a real gap.',
  },
  engines_online: {
    label: 'Engines Online',
    definition:
      "The turn by which your value engines (repeatable card draw and mana) are producing more than a stock deck's one land and one card a turn.",
    computed:
      'Computed by simulating each engine card\'s output for turns 0 through 7. "T7+" means not within that window. The extra figure ("+x.x extra turns") is the total surplus over the whole window.',
    caveat: 'With no card profiles it falls back to a coarser estimate from card-advantage and ramp counts.',
  },
  power_scale: {
    label: 'Power Scale',
    definition: 'Where the deck sits within its bracket: low, mid or high.',
    computed:
      'From a rough 1-10 power estimate (with a boost of up to +3 for combos rated for bracket 4+ tables), against cut points measured on owner-labelled decks for each bracket.',
    caveat: "The estimate never places a deck in a bracket; only the rules and the owner's declaration do.",
  },
  bracket: {
    label: 'Bracket',
    definition: "Wizards of the Coast's official Commander Bracket (B1-B5).",
    computed:
      "Ours shows the bracket WotC's hard rules allow (Game Changers, mass land denial, extra turns, two-card combos). Bracket 1 and 5 come only from the owner's declaration.",
  },
  role_value: {
    label: 'Role Value',
    definition:
      'How much resource value a card produces for its role, measured in Value Units: a card that draws one card per turn is worth about 1.0 RV.',
    computed:
      'It compares cards within the same role, not across roles. Shown per card in role suggestions and as "Avg RV" across your revisions. "-" means no value profile for that card.',
    caveat: 'Role Value builds on the Value Units concept coined by the author of commandertemplate.com.',
  },
  role_quality: {
    label: 'Role Quality',
    definition: 'How well a card does its role, 0 to 1, computed from what the card does: speed, mana value, how much it can hit, how permanently, and whether it repeats.',
    computed: "Derived from the card's classified shape and its Scryfall Tagger tags; one number per role, compared only within that role. With no classification yet, it is read from the type line and tags alone.",
  },
  plan: {
    label: 'Adoption hold',
    definition: "The adoption hold: how much of this commander's decks run the card you would cut, from EDHREC.",
    computed: 'Raises the bar a new card must clear to replace it (1.5 x the share). It never ranks the new card itself, and a commander without an EDHREC page adds nothing.',
  },
  profile: {
    label: 'Profile',
    definition: "The deck plan's best pull on the card, 0 to 1: how much denser the deck's archetypes and its commander run the strongest thing this card does (a fact, a subtype or a tag) than other decks of its colors. The plan's archetypes are named after EDHREC's themes when one matches the deck.",
    computed:
      "Added to the score at 2.0 x the pull (never multiplied) in What's New (the builder's blend does not use it; the chip is a label); it breaks exact ties between decks in Find a Home. Only the card's best reason counts, so a card with many unrelated facts does not score higher for having them, and the plan never subtracts.",
  },
  salt: {
    label: 'Salt',
    definition: "EDHREC's measure of how much players dislike playing against a card.",
    computed:
      'A card is flagged when it is above the tolerance you set, or at a default awareness level of 2.0 if you set none.',
    caveat: 'Lags behind new sets.',
  },
  budget: {
    label: 'Budget',
    definition: 'Flagged "strain" when a card costs more than the per-card budget you set.',
    computed: 'If the price is unknown or no budget is set, it reads fine.',
  },
  reads_as: {
    label: 'Reads as',
    definition:
      "Which bracket of analyzed decks this deck's facet scores most resemble, and where it sits among those decks' spread: the lower, middle or upper third.",
    computed:
      'Computed against the median and the 10th-90th percentile spread of analyzed decks at each bracket; the nearest medians win. The tooltip says how many decks that rests on.',
    caveat:
      'The samples are small; a bracket with fewer than 5 decks is not used. Advisory only; your bracket is your choice.',
  },
  theme: {
    label: 'Theme',
    definition: "On EDHREC's theme page for this commander — how played, not how good.",
    computed:
      "Computed from the card's inclusion on the EDHREC theme pages that match this deck's themes. 0 means it is not listed there, which says nothing about its quality.",
    caveat: 'An adoption signal: it favors cards players already run and lags behind new sets.',
  },
  popularity: {
    label: 'Popularity',
    definition: "How often EDHREC decks for this commander play the card — how played, not how good.",
    computed: "Computed from the card's inclusion rate on EDHREC's page for this commander. 0 means not listed there.",
    caveat: 'An adoption signal: it favors long-established cards and lags behind new sets.',
  },
  blend_score: {
    label: 'Blend score',
    definition: "The builder's ranking number, 0-100: half EDHREC inclusion and half co-play from the deck's cards, or co-play alone when there is no EDHREC page for the commander.",
    computed:
      "Computed as 0.5 x inclusion + 0.5 x co-play (each 0 to 1), shown x 100. The chips beside it are labels, not parts of the ranking. A spicy pick keeps its place when it ranks in the list on its own; one added at the end can score lower than the row above it.",
  },
  spicy: {
    label: 'Spicy',
    definition: "A low-inclusion card (under 15% of this commander's decks) whose co-play with your cards clears a measured bar.",
    computed: "It keeps its place when it ranks in the list on its own, and is added at the end when its low inclusion would otherwise keep it out. Usually none appear. The tooltip gives its share of this commander's decks.",
  },
  combo_completion: {
    label: 'Combo',
    definition: 'How much adding this card moves the deck toward completing a combo.',
    computed: 'Computed from Commander Spellbook combos whose other pieces are already in the deck. 0 means it completes or advances none.',
  },
  role_need: {
    label: 'Role need',
    definition: "How well the card fits this role, weighted by how many slots of that role the deck's bracket template still needs.",
    computed: "Computed from the card's tagger tags against the role. A half fit means only the broad anchor tag matched.",
  },
  goal_target: {
    label: 'Goal target',
    definition: 'The position you want a facet to reach. It is a position, not a limit: being above it costs nothing.',
    computed:
      "Your stated target if you set one; otherwise the median (p50) of the decks analyzed at this build's bracket. The gap is how far the build sits below the target (0 when at or above it).",
    caveat:
      'The shaded region is where the middle 80% of those decks sit (p10 to p90), with their median ticked. You may set a target outside it; it is shown, never clamped.',
  },
  trial_goals: {
    label: 'Trial goals',
    definition: 'A what-if: goal values you are trying out that are not saved anywhere.',
    computed:
      'The build is re-measured against the trial goals, which replace the saved ones for that measurement. Save goals to keep them, or spawn a variant that carries them.',
    caveat: 'Leaving without saving discards a trial.',
  },
  inputs_staleness: {
    label: 'Stale inputs',
    definition: 'The numbers lean on a deck analysis that is not for this exact list.',
    computed:
      "How focused the deck is on its commander (an input to Closing), win turn and commander salt come from the saved deck analysis of the base list. They are marked stale when you are looking at a variant, or when the base list has changed since that analysis ran.",
    caveat: 'Re-run the deck analysis to refresh them.',
  },
  deck_budget: {
    label: 'Deck budget',
    definition: 'A ceiling on what the whole deck costs, separate from the per-card Budget.',
    computed:
      'The sum of quantity x Scryfall USD price over every priced card. Cards with no price are not in the sum, so with any unpriced cards the total is a lower bound.',
    caveat: 'Flagged "strain" when the total is above the ceiling you set.',
  },
  eval_margin: {
    label: 'Margin',
    definition: "How far the card's score sits above (+) or below (-) the bar it was measured against.",
    computed:
      "The card's score minus the score of the deck's weakest card in that role (or, when the role has room or nothing to displace, the deck's median for that role). 0.00 is a tie.",
    caveat: 'Positive beats the bar; it is a gap in score, not a probability of being better in play.',
  },
  cut_damage: {
    label: 'Cut damage',
    definition: "How much worse the deck's facets get if you remove that card.",
    computed:
      "Computed by re-scoring the deck without it. 0.00 means removing it hurts none of the deck's facets, which is common on a tuned deck. It is shown beside a cut, not used to order the list: cards sharing the added card's role come first, the weakest fit first.",
    caveat: 'A suggestion, not an instruction.',
  },
};

/** Goals & Fit copy that is not a ranking term. Functions build the lines that carry data. */
export const GOALS_COPY = {
  heading: 'Goals & Fit',
  intro: 'How this build sits against the goals you set. Absent goals use the median of decks at this bracket.',
  fieldBudget: 'Per-card budget (USD)',
  fieldSalt: 'Salt tolerance',
  fieldThreshold: 'Operational threshold (turn)',
  fieldDeckBudget: 'Deck budget (USD)',
  facetTargetLabel: (facetLabel: string) => `${facetLabel} target`,
  /** The line beside the Reads-as chip (the chip's tooltip carries the sample size). */
  readsAs: (bracket: number, position: string) => `B${bracket} · ${READS_AS_POSITION_COPY[position] ?? position}`,
  loading: 'Loading the goals fit…',
  gapLine: (gap: string | null) => `gap ${gap ?? '—'}`,
  metaNotesLabel: 'Meta notes',
  metaNotesOptional: 'Optional — useful if you play with a regular pod',
  metaNotesDeckLevel: 'Meta notes belong to the deck; edit them on the base build.',
  outsideRegion: "outside the bracket's region",
  sourceGoal: 'your goal',
  sourceMedian: 'bracket median',
  sourceNone: 'no target',
  noFence: 'no bracket region for this facet',
  lowConfidence: 'low confidence',
  degraded: 'rests on missing inputs',
  thresholdUnknown: "can't judge — the target is beyond the measured horizon",
  thresholdOk: (turn: number | null, limit: number) => `engines online by T${turn ?? '?'} — within your T${limit} threshold`,
  thresholdLate: (turn: number | null, limit: number) =>
    turn === null ? `engines not online within the measured horizon — later than your T${limit} threshold`
      : `engines online T${turn} — later than your T${limit} threshold`,
  trialToggle: 'Try goals',
  trialBanner: 'Trial goals — not saved. Numbers below answer these what-if values.',
  save: 'Save goals',
  saving: 'Saving…',
  spawn: 'Spawn variant with these goals',
  spawning: 'Spawning…',
  discard: 'Discard trial',
  useDeckGoals: 'Use deck goals',
  noGoalsHere: 'No goals for this variant',
  variantGoalsNote:
    'Use deck goals follows the deck, including later changes. No goals for this variant keeps it goal-free even when the deck has goals.',
  variantGoalsStep: 'Give this variant its own goals',
  variantGoalsStepNote: 'Prefilled from the goals in effect for this build. Leave off to follow the deck.',
  deckPriceLine: (price: string, unpriced: number) =>
    `deck price $${price}${unpriced > 0 ? ` — lower bound: ${unpriced} card${unpriced === 1 ? '' : 's'} unpriced` : ''}`,
} as const;

/** Synergy-state copy for New Cards, where `page_unavailable` means no measurement exists for this card at all
 *  (the Find a Home copy, "no EDHREC data for this commander", would mislead here). */
export const NEW_CARDS_SYNERGY_PRESENCE_COPY: Record<Exclude<SynergyPresence, 'measured'>, string> = {
  ...SYNERGY_PRESENCE_COPY,
  page_unavailable: 'no synergy measurement exists for this card yet',
};

export type NewCardLandReason = 'tapland_discipline' | 'typed_fetch_support' | 'no_land_case';

/** Why a new land was held back, shown beside its name. */
export const NEW_CARD_LAND_REASON_COPY: Record<NewCardLandReason, string> = {
  tapland_discipline: "held to the deck's land discipline: it enters tapped and this deck runs few taplands",
  typed_fetch_support: "a basic-typed land that supports this deck's fetches",
  no_land_case: "held to the deck's land discipline: no case for another land slot",
};

/** New Cards tab copy that is not a ranking term. Functions build the lines that carry data. */
export const NEW_CARDS_COPY = {
  heading: "What's New for this deck",
  analyzeButton: 'Analyze New Cards',
  reanalyzeButton: 'Re-analyze New Cards',
  analyzing: 'Analyzing…',
  analyzingNote: 'Analyzing new cards for your deck…',
  makeRoom: 'Make room',
  makeRoomIn: (deck: string) => `Make room in ${deck}`,
  unreleased: 'Unreleased',
  badge: (n: number) => `${n} new ${n === 1 ? 'pick' : 'picks'}`,
  openTab: "Open What's New",
  competesIn: (role: string) => `Competes in ${role} with:`,
  failedPrefix: 'The new-cards analysis failed:',
  failedStale: 'Showing the previous analysis.',
  olderFormat: 'This result predates the current format; it will refresh with the next deck analysis.',
  synergyNa: 'n/a',
  barLine: (name: string, roleLabel: string) => `beats ${name}, the weakest ${roleLabel}`,
  straightAdd: (roleLabel: string) => `straight add — the deck has no incumbent to displace for ${roleLabel}`,
  staleNotice: 'Newer sets have been released since this analysis; re-run the deck analysis to include them.',
  analyzedLine: (cards: number, sets: string) => `Analyzed ${cards} cards from ${sets}`,
  browseLoading: 'Loading recent set cards…',
  browseEmpty: "No new cards found for this deck's color identity.",
} as const;

/** Dec-a-Sketch copy. Seed outcomes reuse the evaluate-cards verdict copy (EVALUATE_COPY) wherever the state is the same. */
export const DAS_COPY = {
  intro: "Specify a commander, a theme, or both. We'll suggest 3 concepts, then build a full 100-card list from your stored card data, measured against the goals below.",
  deckBudgetLabel: 'Deck budget (USD)',
  perCardLabel: 'Per-card cap (USD)',
  unlimited: 'Unlimited',
  budgetHint: 'Leave blank for no limit. The mana base is not counted against the deck budget.',
  clear: 'Clear',
  building: 'Building your deck from stored card data…',
  buildingNote: 'This is quick and costs 1 analysis credit when it finishes. You can navigate away and return via the notification bell.',
  olderFormat: 'This sketch was generated by an older version, so it has no full report. Run a new sketch to see the seed outcomes, role fill and fit report.',
  seedsHeading: 'Seed cards',
  seedIncluded: 'Included',
  cardCount: (n: number) => `${n} cards`,
  roleFillHeading: 'Role fill',
  roleFillLine: (filled: number, target: number) => `${filled} of ${target}`,
  shortfallsLine: (roles: string) => `Short on: ${roles}`,
  fitHeading: 'Fit against your goals',
  priceLine: (total: string, unpriced: number) =>
    unpriced > 0 ? `$${total} (${unpriced} unpriced card${unpriced === 1 ? '' : 's'} not counted)` : `$${total}`,
  landPriceLine: (land: string) => `includes $${land} mana base, not counted against the budget`,
  budgetOf: (cap: string) => `of $${cap} budget`,
  notesHeading: 'Notes',
  logSummary: (n: number) => `Build log (${n} steps)`,
  saveNamePlaceholder: 'Deck name…',
  saveDeck: 'Save Deck',
  saving: 'Saving…',
  reject: 'Reject',
  startOver: 'Start over',
  saved: 'Deck saved!',
  deckFailed: (msg: string) => `The deck was not created: ${msg}`,
  bracketFailed: (msg: string) => `Deck created, but its bracket did not save (${msg}). Set it from the Build tab.`,
  themesFailed: (msg: string) => `Deck created, but its themes did not save (${msg}). Choose them from the Build tab.`,
  goalsFailed: (msg: string) => `Deck created and bracket saved, but its goals did not save (${msg}). Set them from the Build tab.`,
  runFailed: (msg: string) => `Deck created with its bracket and goals, but the sketch run was not marked saved (${msg}).`,
  openDeck: 'Open the deck',
  offcolorRefs: (commander: string) => `won't make the deck — outside ${commander}'s colors`,
  themesUsed: 'Themes used',
  overlap: (n: number, of: number) => `${n} of the commander's ${of} most-played cards on EDHREC`,
  thinPool: "Some chosen themes have little stored card data, so the list fills from a shallow pool. Warming those themes' EDHREC data would deepen it.",
  curveHeading: 'Curve',
  curveAvgMv: (mv: string) => `Average mana value ${mv}`,
  curveDevelopment: (pct: string) => `P(keepable development) ${pct}%`,
  thinPips: (pips: string) => `Only a couple of sources for ${pips}`,
  seedPipUncastable: 'Left out',
  seedPipUncastableLine: (pip: string) => `Needs ${pip} mana the land base can't make — left out`,
  seedUnknown: 'Unrecognised',
  curveAnswer: (pct: string, horizon: number) => `P(drawing an answer) ${pct}% by turn ${horizon}`,
} as const;

/** The most card names one evaluate-these-cards request carries (the server enforces the same cap). */
export const EVALUATE_MAX_CARDS = 15;


/** Evaluate-these-cards copy that is not a ranking term. Functions build the lines that carry data. */
export const EVALUATE_COPY = {
  heading: 'Evaluate these cards',
  intro: 'Type cards you are considering to see how each stacks up against what this deck already runs.',
  placeholder: 'Add a card name…',
  add: 'Add',
  remove: (name: string) => `Remove ${name}`,
  capReached: `At most ${EVALUATE_MAX_CARDS} cards at a time — remove one to add another.`,
  evaluate: 'Evaluate',
  evaluating: 'Evaluating…',
  degraded: 'rests on missing inputs',
  verdictLabel: {
    beats_bar: 'Beats the bar',
    role_short: 'Fills a short role',
    below_bar: 'Below the bar',
    no_role_fit: 'No role fit',
    unscored: 'Unscored',
    land_admitted: 'Land admitted',
    land_held: 'Land held back',
    not_found: 'Not found',
    already_in_deck: 'Already in the deck',
    color_identity: 'Outside colour identity',
    not_legal: 'Not legal',
  },
  belowBarIncumbent: (roleLabel: string, name: string, score: number) =>
    `doesn't beat your weakest ${roleLabel} — ${name} (${score.toFixed(2)})`,
  belowBarMedian: (roleLabel: string, median: number) =>
    `below the deck's ${roleLabel} median (${median.toFixed(2)})`,
  noRoleFit: "this card scores in no role this deck's template asks for",
  unscored: 'no tagger tags yet, so it cannot be scored',
  landAdmitted: "a land the deck's land discipline would admit",
  notFound: (requested: string) => `no card named "${requested}"`,
  alreadyInDeck: 'already in this deck',
  colorIdentity: "outside your commander's colour identity",
  notLegal: 'not legal in Commander',
  scoreLine: (score: number) => `score ${score.toFixed(2)}`,
  /** Signed two-decimal margin; an exact tie (to the printed digits) is an unsigned 0.00. */
  margin: (m: number) => {
    const fixed = Math.abs(m).toFixed(2);
    if (Number(fixed) === 0) return '0.00';
    return `${m > 0 ? '+' : '−'}${fixed}`;
  },
  narrate: 'Explain these verdicts',
  narrateAgain: 'Explain again',
  narrating: 'Explaining…',
  narratingNote: 'Reading the verdicts against your deck…',
  narrateIdle: 'Ask for a short AI explanation of why each card got its verdict. Uses 1 analysis credit when it answers.',
  narrateEmpty: 'No explanation came back. Nothing was charged; the verdicts above stand on their own.',
  narrateCredits: 'Out of analysis credits.',
  upgrade: 'Upgrade to Pro',
} as const;
